import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import * as Api from "@/api";
import Avatar from "@/design/components/Avatar";
import { showSnackbar } from "@/store/reducers/app";
import { deleteDashboardQueue, toggleDashboardQueue, toggleQueueExpandState } from "@/store/reducers/dashboard";
import useInterval from "@/utils/hooks/useInterval";
import Valkyrie, { viChevronDown, viGripDotsV, viList, viPeople, viPhone, viTrashCan } from "@sippy-platform/valkyrie";

import { useSortable } from "@dnd-kit/react/sortable";
import { getAuth, onAuthStateChanged } from "@firebase/auth";
import { collection, getFirestore, onSnapshot, query } from "@firebase/firestore";
import {
  Card,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Tab,
  Tabs,
} from "@mui/material";
import PropTypes from "prop-types";

import CallerTab from "./_CallerTab";
import MemberTab from "./_MemberTab";
import AddMemberFlyout from "./AddMemberFlyout";

function QueueCard({ dragMode, id, index, ...props }) {
  const dispatch = useDispatch();

  const { ref, isDragging } = useSortable({ id, index });

  const pbx = useSelector((state) => state.domain.pbx);
  const dashboardSettings = useSelector((state) => state.dashboard.find((_pbx) => _pbx.context === pbx.context));
  const queueSettings = useSelector((state) =>
    state.dashboard
      .find((_pbx) => _pbx.context === pbx.context)
      ?.queueList.find((_queue) => _queue.id === props.queue?.id),
  );

  const [errorWhileLoading, setErrorWhileLoading] = useState();
  const [tab, setTab] = useState(0);
  const [queue, setQueue] = useState({});
  const [calls, setCalls] = useState([]);

  useEffect(() => {
    async function getData() {
      await Promise.resolve(Api.getQueueSilent({ id: props.queue?.id }))
        .then((response) => {
          setQueue(response.data.queue);
          setErrorWhileLoading(undefined);
        })
        .catch((error) => {
          if (error.request.status === 404) {
            dispatch(deleteDashboardQueue({ pbx: pbx.context, queueId: props.queue?.id }));
          } else {
            setErrorWhileLoading(true);
          }
        });
    }

    getData();
  }, [dispatch, pbx.context, props.queue?.id]);

  useEffect(() => {
    let unsubscribeActivity;

    const auth = getAuth();
    let unsubscribeUser = onAuthStateChanged(auth, (user) => {
      if (user && queue.exten) {
        let db = getFirestore();
        unsubscribeActivity = onSnapshot(
          query(collection(db, `states/${pbx.context}/queues/${queue.exten}/calls`)),
          (docs) => {
            const calls = [];
            docs.forEach((doc) => calls.push(doc.data()));
            setCalls(calls);
          },
          (error) => {
            dispatch(showSnackbar(error.toString()));
          },
        );
      }
    });

    return () => {
      if (unsubscribeActivity) unsubscribeActivity();
      unsubscribeUser();
    };
  }, [dispatch, pbx.context, queue.exten]);

  useInterval(
    () => {
      if (errorWhileLoading !== 404) {
        Promise.resolve(Api.getQueueSilent({ id: props.queue?.id })).then((response) => {
          setQueue(response.data.queue);
          setErrorWhileLoading(undefined);
        });
      }
    },
    // We'll always try to refresh every minute for a queue that couldn't load
    (errorWhileLoading ? 1 : dashboardSettings.settings.refreshDelay) * 60000,
  );

  if (errorWhileLoading) {
    return (
      <Card>
        <List>
          <ListItem
            secondaryAction={
              <IconButton
                onClick={() => dispatch(toggleDashboardQueue({ pbx: pbx.context, queueId: props.queue?.id }))}
              >
                <Valkyrie icon={viTrashCan} />
              </IconButton>
            }
          >
            <ListItemText primary="The queue couldn't be loaded" secondary="We'll try again in a minute." />
          </ListItem>
        </List>
      </Card>
    );
  }

  if (queue.name === undefined) {
    return (
      <Card>
        <List>
          <ListItem disablePadding secondaryAction={<Skeleton variant="circular" width={24} height={24} />}>
            <ListItemButton disabled>
              <ListItemAvatar>
                <Skeleton variant="circular" width={32} height={32} />
              </ListItemAvatar>
              <ListItemText
                disableTypography
                primary={<Skeleton height={18} width="80%" />}
                secondary={<Skeleton height={12} width="40%" />}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Card>
    );
  }

  return (
    <Card
      ref={dragMode ? ref : undefined}
      id={`queueCard${queue.exten}`}
      sx={(theme) => ({
        ...(isDragging && {
          boxShadow: 1,
          backdropFilter: "blur(5px)",
          backgroundColor: "rgb(248 248 248 / .5) !important",
          ...theme.applyStyles("dark", {
            backgroundColor: "rgb(40 40 45 / .5) !important",
          }),
          position: "relative",
          zIndex: 1,
        }),
      })}
    >
      <List>
        <ListItem
          disablePadding
          secondaryAction={
            !dragMode && (
              <IconButton
                onClick={() => dispatch(toggleQueueExpandState({ pbx: pbx.context, queueId: props.queue?.id }))}
                disabled={!queue.name}
              >
                <Valkyrie icon={viChevronDown} rotate={queueSettings.expanded ? 180 : 0} />
              </IconButton>
            )
          }
        >
          <ListItemButton
            sx={[dragMode && { cursor: "move" }]}
            disabled={!queue.name}
            onClick={() => dispatch(toggleQueueExpandState({ pbx: pbx.context, queueId: props.queue?.id }))}
          >
            <ListItemAvatar>
              <Avatar text={queue.name} icon={viList} />
            </ListItemAvatar>
            <ListItemText
              primary={queue.name || "Loading queue..."}
              secondary={
                queue.name
                  ? `${queue.exten} \u00b7 ${queue.members.length} member${
                      queue.members.length === 1 ? "" : "s"
                    } \u00b7 ${calls.length} caller${calls.length === 1 ? "" : "s"}`
                  : "Fetching details..."
              }
            />
            {dragMode && (
              <ListItemIcon sx={{ justifyContent: "flex-end" }}>
                <Valkyrie icon={viGripDotsV} />
              </ListItemIcon>
            )}
          </ListItemButton>
        </ListItem>
      </List>
      <Collapse in={queueSettings.expanded && !dragMode} timeout="auto" unmountOnExit>
        <Tabs value={tab} onChange={(e, tab) => setTab(tab)} sx={{ m: 1, mt: 0 }}>
          <Tab label="Members" icon={<Valkyrie icon={viPeople} />} />
          <Tab label="Callers" icon={<Valkyrie icon={viPhone} />} />

          <AddMemberFlyout queue={queue} setQueue={setQueue} />
        </Tabs>
        <Divider sx={{ borderColor: (theme) => theme.vars.palette.divider }} />
        {tab === 0 && <MemberTab queue={queue} setQueue={setQueue} />}
        {tab === 1 && <CallerTab calls={calls} />}
      </Collapse>
    </Card>
  );
}

QueueCard.propTypes = {
  id: PropTypes.number,
  index: PropTypes.number,
  queue: PropTypes.object,
  dragMode: PropTypes.bool,
  isActive: PropTypes.bool,
};

export default QueueCard;
